import './index.css';
import { localeManager, t } from '../../services/locale';
import { themeManager } from '../../services/theme';
import type { ThemeConfig } from '../../themes/types';
import type { IBimComponent } from '../../types/component';
import { getIcon } from '../../utils/icon-manager';
import type { ManagerRegistry } from '../../core/manager-registry';
import { ComponentTreeDrawerConstructTreeTab } from './construct-tree-tab';
import { ComponentTreeDrawerPinTab } from './pin-tab';
import type {
    ComponentTreeDrawerOptions,
    ComponentTreeDrawerPinTabApi,
    ComponentTreeDrawerTabApi,
    ComponentTreeDrawerTabComponent,
    ComponentTreeDrawerTabId
} from './types';
import { ComponentTreeDrawerViewTab } from './view-tab';

const TAB_ORDER: ComponentTreeDrawerTabId[] = ['view', 'pin', 'construct-tree'];

export class ComponentTreeDrawer implements IBimComponent {
    public readonly element: HTMLElement;

    private readonly options: ComponentTreeDrawerOptions;
    private readonly headerElement: HTMLElement;
    private readonly tabsElement: HTMLElement;
    private readonly closeButton: HTMLButtonElement;
    private readonly bodyElement: HTMLElement;
    private readonly tabButtons: Map<ComponentTreeDrawerTabId, HTMLButtonElement> = new Map();
    private readonly panelElements: Map<ComponentTreeDrawerTabId, HTMLElement> = new Map();
    private readonly tabInstances: {
        view: ComponentTreeDrawerTabComponent;
        pin: ComponentTreeDrawerPinTabApi & ComponentTreeDrawerTabComponent;
        'construct-tree': ComponentTreeDrawerTabComponent;
    };
    private unsubscribeLocale: (() => void) | null = null;
    private unsubscribeTheme: (() => void) | null = null;
    private activeTab: ComponentTreeDrawerTabId;
    private open = false;

    constructor(options: ComponentTreeDrawerOptions = {}) {
        this.options = options;
        this.activeTab = options.defaultTab ?? 'view';
        const registry = options.registry as ManagerRegistry | undefined;
        this.tabInstances = {
            view: new ComponentTreeDrawerViewTab(registry ?? null),
            pin: new ComponentTreeDrawerPinTab(registry ?? null),
            'construct-tree': new ComponentTreeDrawerConstructTreeTab(registry ?? null)
        };

        this.element = document.createElement('aside');
        this.element.className = 'component-tree-drawer';
        this.element.setAttribute('aria-hidden', 'true');

        this.headerElement = document.createElement('div');
        this.headerElement.className = 'component-tree-drawer-header';

        this.tabsElement = document.createElement('div');
        this.tabsElement.className = 'component-tree-drawer-tabs';

        this.closeButton = document.createElement('button');
        this.closeButton.type = 'button';
        this.closeButton.className = 'component-tree-drawer-close';
        this.closeButton.innerHTML = getIcon('close');
        this.closeButton.addEventListener('click', () => this.close());

        this.bodyElement = document.createElement('div');
        this.bodyElement.className = 'component-tree-drawer-body';

        this.headerElement.appendChild(this.tabsElement);
        this.headerElement.appendChild(this.closeButton);
        this.element.appendChild(this.headerElement);
        this.element.appendChild(this.bodyElement);

        this.renderTabs();
        this.renderPanels();
    }

    public init(): void {
        const container = this.options.container ?? document.body;
        if (!this.element.parentElement) {
            container.appendChild(this.element);
        }

        Object.values(this.tabInstances).forEach((tab) => tab.init());
        this.setLocales();
        this.setTheme(themeManager.getTheme());
        this.applyActiveTab();
        this.applyOpenState();
        this.unsubscribeLocale = localeManager.subscribe(() => this.setLocales());
        this.unsubscribeTheme = themeManager.subscribe((theme) => this.setTheme(theme));
    }

    public setTheme(theme: ThemeConfig): void {
        const style = this.element.style;
        style.setProperty('--bim-component-tree-bg', theme.bgGlass || theme.bgElevated);
        style.setProperty('--bim-component-tree-blur', theme.bgGlassBlur || '18px');
        style.setProperty('--bim-component-tree-border', theme.borderDefault);
        style.setProperty('--bim-component-tree-shadow', theme.shadowLg);
        style.setProperty('--bim-component-tree-text', theme.textPrimary);
        style.setProperty('--bim-component-tree-muted', theme.textSecondary);
        style.setProperty('--bim-component-tree-tab-bg', theme.componentBg);
        style.setProperty('--bim-component-tree-tab-hover', theme.componentBgHover);
        style.setProperty('--bim-component-tree-tab-active', theme.primarySubtle);
        style.setProperty('--bim-component-tree-tab-active-border', theme.primary);
        style.setProperty('--bim-component-tree-tab-active-text', theme.primary);
        style.setProperty('--bim-component-tree-close-bg', theme.componentBg);
        style.setProperty('--bim-component-tree-close-hover', theme.componentBgHover);
        style.setProperty('--bim-component-tree-surface', theme.bgElevated);
        style.setProperty('--bim-component-tree-surface-border', theme.borderSubtle);
        style.setProperty('--bim-component-tree-subtab-nav-bg', theme.tabBg);
        style.setProperty('--bim-component-tree-subtab-nav-border', theme.borderDefault);
        style.setProperty('--bim-component-tree-subtab-text', theme.textSecondary);
        style.setProperty('--bim-component-tree-subtab-hover-bg', theme.tabItemBgHover);
        style.setProperty('--bim-component-tree-subtab-hover-text', theme.textPrimary);
        style.setProperty('--bim-component-tree-subtab-active-bg', theme.tabItemBgActive);
        style.setProperty('--bim-component-tree-subtab-active-text', theme.tabItemTextActive);
        style.setProperty('--bim-component-tree-subtab-active-border', theme.primarySubtle);
        style.setProperty('--bim-component-tree-subtab-active-shadow', theme.shadowSm);
        style.setProperty('--bim-component-tree-tree-bg', theme.bgElevated);
        style.setProperty('--bim-component-tree-tree-border', theme.borderDefault);
    }

    public setLocales(): void {
        for (const tabId of TAB_ORDER) {
            const button = this.tabButtons.get(tabId);
            if (!button) continue;
            button.textContent = this.getTabTitle(tabId);
        }

        const closeLabel = t('componentTreeDrawer.actions.close');
        this.closeButton.setAttribute('aria-label', closeLabel);
        this.closeButton.setAttribute('title', closeLabel);
    }

    public destroy(): void {
        if (this.unsubscribeLocale) {
            this.unsubscribeLocale();
            this.unsubscribeLocale = null;
        }
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }
        Object.values(this.tabInstances).forEach((tab) => tab.destroy());
        this.tabButtons.clear();
        this.panelElements.clear();
        this.element.remove();
    }

    public openDrawer(tabId?: ComponentTreeDrawerTabId): void {
        if (tabId) {
            this.activeTab = tabId;
            this.applyActiveTab();
        }
        this.open = true;
        this.applyOpenState();
        void this.tabInstances[this.activeTab].onShow?.();
    }

    public close(): void {
        if (!this.open) return;
        this.open = false;
        this.applyOpenState();
        this.options.onClose?.();
    }

    public toggle(tabId?: ComponentTreeDrawerTabId): void {
        if (this.open) {
            this.close();
            return;
        }
        this.openDrawer(tabId);
    }

    public isOpen(): boolean {
        return this.open;
    }

    public getActiveTab(): ComponentTreeDrawerTabId {
        return this.activeTab;
    }

    public switchTab(tabId: ComponentTreeDrawerTabId): void {
        if (this.activeTab === tabId) return;
        this.activeTab = tabId;
        this.applyActiveTab();
        void this.tabInstances[tabId].onShow?.();
        this.options.onTabChange?.(tabId);
    }

    public getViewTabApi(): ComponentTreeDrawerTabApi {
        return this.tabInstances.view;
    }

    public getPinTabApi(): ComponentTreeDrawerPinTabApi {
        return this.tabInstances.pin;
    }

    public setPinEditable(editable: boolean): void {
        this.getPinTabApi().setEditable(editable);
    }

    public isPinEditable(): boolean {
        return this.getPinTabApi().isEditable();
    }

    public getConstructTreeTabApi(): ComponentTreeDrawerTabApi {
        return this.tabInstances['construct-tree'];
    }

    private renderTabs(): void {
        this.tabsElement.innerHTML = '';
        for (const tabId of TAB_ORDER) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'component-tree-drawer-tab';
            button.dataset.tab = tabId;
            button.addEventListener('click', () => this.switchTab(tabId));
            this.tabButtons.set(tabId, button);
            this.tabsElement.appendChild(button);
        }
    }

    private renderPanels(): void {
        this.bodyElement.innerHTML = '';
        for (const tabId of TAB_ORDER) {
            const panel = document.createElement('div');
            panel.className = 'component-tree-drawer-panel';
            panel.dataset.tab = tabId;
            panel.appendChild(this.tabInstances[tabId].getElement());
            this.panelElements.set(tabId, panel);
            this.bodyElement.appendChild(panel);
        }
    }

    private applyActiveTab(): void {
        for (const tabId of TAB_ORDER) {
            const isActive = tabId === this.activeTab;
            this.tabButtons.get(tabId)?.classList.toggle('is-active', isActive);
            this.panelElements.get(tabId)?.classList.toggle('is-active', isActive);
        }
    }

    private applyOpenState(): void {
        this.element.classList.toggle('is-open', this.open);
        this.element.setAttribute('aria-hidden', `${!this.open}`);
    }

    private getTabTitle(tabId: ComponentTreeDrawerTabId): string {
        const keyMap: Record<ComponentTreeDrawerTabId, string> = {
            view: 'componentTreeDrawer.tabs.view',
            pin: 'componentTreeDrawer.tabs.pin',
            'construct-tree': 'componentTreeDrawer.tabs.constructTree'
        };
        return t(keyMap[tabId]);
    }
}
